import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Mail, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL } from "@/constants";

const DeactivatedAccount: React.FC = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { t } = useTranslation();

    const handleBackToLogin = async () => {
        await logout();
        navigate("/login");
    };

    const handleContactSupport = () => {
        window.location.href = "mailto:support@clipmanagement.com";
    };

    return (
        <div className="min-h-screen bg-white font-sans selection:bg-red-100 relative overflow-hidden flex items-center justify-center p-4">

            {/* Animated Background Blobs (Toni Rossi/Arancio per indicare Alert) */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <motion.div
                    animate={{
                        x: [0, 100, 0],
                        y: [0, -50, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-red-100/40 rounded-full blur-[100px]"
                />
                <motion.div
                    animate={{
                        x: [0, -100, 0],
                        y: [0, 100, 0],
                        scale: [1, 1.3, 1],
                    }}
                    transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-orange-100/30 rounded-full blur-[120px]"
                />
            </div>

            {/* Navbar Minimal (Solo Logo posizionato fuori dalla card) */}
            <nav className="absolute top-0 left-0 right-0 p-6 flex justify-center z-50">
                <img src={LOGO_URL} alt="Clip Logo" className="h-8 w-auto object-contain" />
            </nav>

            {/* Main Card */}
            <div className="relative z-10 w-full max-w-lg mt-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white/80 backdrop-blur-xl border border-gray-100 shadow-2xl shadow-gray-200/50 rounded-3xl p-8 md:p-12 text-center"
                >
                    {/* Icon Animation (Cerchio Rosso, Icona Bianca) */}
                    <div className="mb-8 flex justify-center">
                        <div className="relative">
                            {/* Main Red Circle */}
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 20,
                                    delay: 0.1,
                                }}
                                className="w-24 h-24 bg-red-600 rounded-full flex items-center justify-center shadow-xl shadow-red-500/30 relative z-10"
                            >
                                {/* White X Icon with delayed pop */}
                                <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 20 }}
                                >
                                    <X className="text-white w-12 h-12" strokeWidth={3} />
                                </motion.div>
                            </motion.div>
                            {/* Ripple effect */}
                            <motion.div
                                animate={{
                                    scale: [1, 1.5],
                                    opacity: [0, 0.3, 0]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeOut"
                                }}
                                className="absolute inset-0 bg-red-200 rounded-full z-0"
                            />
                        </div>
                    </div>

                    <motion.h1
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-3xl font-black text-gray-900 mb-4 tracking-tight"
                    >
                        {t('auth.deactivated.title')}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-gray-500 text-lg mb-8 leading-relaxed"
                    >
                        {t('auth.deactivated.subtitle')}<br className="hidden md:block" />
                    </motion.p>

                    {/* Alert Box Pulita */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.35 }}
                        className="mb-8 bg-red-50/50 border border-red-100 rounded-2xl p-4 text-left"
                    >
                        <p className="text-sm text-gray-600 font-medium leading-relaxed">
                            <span className="block text-red-700 font-bold mb-1">{t('auth.deactivated.what_to_do_title')}</span>
                            {t('auth.deactivated.what_to_do_desc')}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-3"
                    >
                        <button
                            onClick={handleContactSupport}
                            className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                        >
                            <Mail size={18} />
                            {t('auth.deactivated.contact_support')}
                        </button>

                        <button
                            onClick={handleBackToLogin}
                            className="w-full py-4 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2 group"
                        >
                            <LogOut size={18} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                            {t('auth.deactivated.logout')}
                        </button>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default DeactivatedAccount;